import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal,
  TextInput, Dimensions, Animated, Platform, StatusBar,
  BackHandler, RefreshControl, Share, Alert, Switch,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── AI SERVICE ───────────────────────────────────────────────────────────────
let aiService = null;
try { aiService = require('../../../services/aiService'); } catch {}

const askReportsAI = async (prompt) => {
  const context = 'You are a business reporting expert for JobEng ERP. Provide concise, actionable summaries and insights for the report requested.';
  if (aiService?.getAIResponse) {
    try { return await aiService.getAIResponse(prompt, context); } catch {}
  }
  try {
    const res = await fetch('https://api.jobeng.ai/api/ai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514', max_tokens: 500,
        system: context,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    const data = await res.json();
    return (data.text || data.content || data.choices?.[0]?.message?.content || '');
  } catch {}
  return 'AI summary: Report generated successfully. Key metrics are within expected ranges. Review the detailed breakdown for actionable insights.';
};


const { width: W } = Dimensions.get('window');

const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6', pink: '#ec4899',
};

const REPORT_CATEGORIES = [
  { id: 'all', label: 'All Reports', icon: 'view-grid' },
  { id: 'finance', label: 'Finance', icon: 'cash-multiple', color: BRAND.success },
  { id: 'crm', label: 'CRM', icon: 'handshake', color: '#f97316' },
  { id: 'hr', label: 'HR', icon: 'account-group', color: BRAND.purple },
  { id: 'inventory', label: 'Inventory', icon: 'warehouse', color: BRAND.mint },
  { id: 'projects', label: 'Projects', icon: 'clipboard-check', color: '#6366f1' },
  { id: 'operations', label: 'Operations', icon: 'cog', color: BRAND.warning },
];

const REPORTS = [
  // Finance
  { id: 'R001', title: 'Profit & Loss Statement', category: 'finance', type: 'standard', period: 'Monthly', size: '2.4 MB', format: 'PDF', status: 'ready', generated: '25 Jan 2025 09:12', color: BRAND.success, icon: 'file-chart', scheduled: true, schedFreq: 'Monthly', tags: ['financial', 'HMRC'] },
  { id: 'R002', title: 'Balance Sheet', category: 'finance', type: 'standard', period: 'Quarterly', size: '1.8 MB', format: 'PDF', status: 'ready', generated: '20 Jan 2025 14:30', color: BRAND.success, icon: 'bank', scheduled: true, schedFreq: 'Quarterly', tags: ['financial'] },
  { id: 'R003', title: 'Cash Flow Analysis', category: 'finance', type: 'standard', period: 'Weekly', size: '0.9 MB', format: 'Excel', status: 'ready', generated: '24 Jan 2025 08:00', color: BRAND.success, icon: 'trending-up', scheduled: true, schedFreq: 'Weekly', tags: ['cash', 'treasury'] },
  { id: 'R004', title: 'Invoice Ageing Report', category: 'finance', type: 'operational', period: 'Weekly', size: '0.6 MB', format: 'Excel', status: 'ready', generated: '23 Jan 2025 07:45', color: BRAND.warning, icon: 'file-document', scheduled: true, schedFreq: 'Weekly', tags: ['invoices', 'AR'] },
  { id: 'R005', title: 'Budget vs Actuals', category: 'finance', type: 'management', period: 'Monthly', size: '3.1 MB', format: 'PDF', status: 'processing', generated: null, color: BRAND.warning, icon: 'chart-bar', scheduled: false, tags: ['budget'] },
  // CRM
  { id: 'R006', title: 'Sales Pipeline Report', category: 'crm', type: 'operational', period: 'Weekly', size: '1.4 MB', format: 'PDF', status: 'ready', generated: '24 Jan 2025 08:30', color: '#f97316', icon: 'trending-up', scheduled: true, schedFreq: 'Weekly', tags: ['pipeline', 'deals'] },
  { id: 'R007', title: 'Customer Segmentation', category: 'crm', type: 'analytical', period: 'Monthly', size: '2.1 MB', format: 'Excel', status: 'ready', generated: '22 Jan 2025 11:00', color: '#f97316', icon: 'account-group', scheduled: false, tags: ['customers', 'segments'] },
  { id: 'R008', title: 'Win/Loss Analysis', category: 'crm', type: 'analytical', period: 'Quarterly', size: '1.7 MB', format: 'PDF', status: 'ready', generated: '15 Jan 2025 14:00', color: '#f97316', icon: 'chart-pie', scheduled: false, tags: ['deals', 'performance'] },
  { id: 'R009', title: 'Sales Rep Performance', category: 'crm', type: 'management', period: 'Monthly', size: '0.8 MB', format: 'PDF', status: 'ready', generated: '20 Jan 2025 09:00', color: '#f97316', icon: 'account-star', scheduled: true, schedFreq: 'Monthly', tags: ['performance', 'team'] },
  // HR
  { id: 'R010', title: 'Headcount & Turnover', category: 'hr', type: 'standard', period: 'Monthly', size: '1.2 MB', format: 'PDF', status: 'ready', generated: '21 Jan 2025 10:15', color: BRAND.purple, icon: 'account-group', scheduled: true, schedFreq: 'Monthly', tags: ['headcount', 'turnover'] },
  { id: 'R011', title: 'Payroll Summary', category: 'hr', type: 'standard', period: 'Monthly', size: '2.8 MB', format: 'Excel', status: 'ready', generated: '31 Dec 2024 23:59', color: BRAND.purple, icon: 'currency-gbp', scheduled: true, schedFreq: 'Monthly', tags: ['payroll', 'HMRC', 'PAYE'] },
  { id: 'R012', title: 'Performance Reviews', category: 'hr', type: 'management', period: 'Quarterly', size: '4.2 MB', format: 'PDF', status: 'ready', generated: '10 Jan 2025 09:00', color: BRAND.purple, icon: 'star', scheduled: false, tags: ['performance', 'reviews'] },
  { id: 'R013', title: 'Leave & Absence Report', category: 'hr', type: 'operational', period: 'Monthly', size: '0.7 MB', format: 'Excel', status: 'ready', generated: '23 Jan 2025 08:00', color: BRAND.purple, icon: 'beach', scheduled: true, schedFreq: 'Monthly', tags: ['leave', 'absence'] },
  // Inventory
  { id: 'R014', title: 'Stock Valuation Report', category: 'inventory', type: 'standard', period: 'Weekly', size: '1.5 MB', format: 'Excel', status: 'ready', generated: '24 Jan 2025 07:00', color: BRAND.mint, icon: 'warehouse', scheduled: true, schedFreq: 'Weekly', tags: ['stock', 'valuation'] },
  { id: 'R015', title: 'Low Stock Alert Report', category: 'inventory', type: 'operational', period: 'Daily', size: '0.3 MB', format: 'PDF', status: 'ready', generated: '25 Jan 2025 06:00', color: BRAND.danger, icon: 'alert', scheduled: true, schedFreq: 'Daily', tags: ['alerts', 'reorder'] },
  // Projects
  { id: 'R016', title: 'Project Status Overview', category: 'projects', type: 'management', period: 'Weekly', size: '2.3 MB', format: 'PDF', status: 'ready', generated: '24 Jan 2025 09:00', color: '#6366f1', icon: 'clipboard-check', scheduled: true, schedFreq: 'Weekly', tags: ['projects', 'status'] },
  { id: 'R017', title: 'Resource Utilisation', category: 'projects', type: 'analytical', period: 'Monthly', size: '1.9 MB', format: 'Excel', status: 'ready', generated: '20 Jan 2025 10:00', color: '#6366f1', icon: 'account-multiple', scheduled: false, tags: ['resources', 'utilisation'] },
  // AI
  { id: 'R018', title: 'AI Executive Summary', category: 'all', type: 'ai', period: 'Weekly', size: '0.5 MB', format: 'PDF', status: 'ready', generated: '25 Jan 2025 07:30', color: '#6366f1', icon: 'robot', scheduled: true, schedFreq: 'Weekly', tags: ['AI', 'executive', 'summary'] },
];

const SCHEDULED = [
  { name: 'P&L Statement', freq: 'Monthly', next: '1 Feb', module: 'Finance', color: BRAND.success },
  { name: 'Cash Flow', freq: 'Weekly', next: 'Mon', module: 'Finance', color: BRAND.success },
  { name: 'Sales Pipeline', freq: 'Weekly', next: 'Mon', module: 'CRM', color: '#f97316' },
  { name: 'Payroll Summary', freq: 'Monthly', next: '31 Jan', module: 'HR', color: BRAND.purple },
  { name: 'Low Stock Alerts', freq: 'Daily', next: 'Tomorrow', module: 'Inventory', color: BRAND.danger },
  { name: 'AI Executive Brief', freq: 'Weekly', next: 'Mon', module: 'AI', color: '#6366f1' },
];

const FORMAT_INFO = { PDF: { color: '#ef4444', icon: 'file-pdf-box' }, Excel: { color: '#10b981', icon: 'microsoft-excel' }, CSV: { color: '#0e4e96', icon: 'file-delimited' }, JSON: { color: '#8b5cf6', icon: 'code-json' } };

const ERPReportsScreen = ({ navigation }) => {
  const [activeTab, setActiveTab] = useState('library');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genForm, setGenForm] = useState({ title: '', category: 'finance', format: 'PDF', period: 'Monthly', includeCharts: true, includeAI: true });
  const [reports, setReports] = useState(REPORTS);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showDetail || showGenerate || showSchedule) { setShowDetail(false); setShowGenerate(false); setShowSchedule(false); return true; }
      return false;
    });
    return () => sub.remove();
  }, [showDetail, showGenerate, showSchedule]);

  const filtered = useMemo(() => {
    let r = [...reports];
    if (activeCategory !== 'all') r = r.filter(x => x.category === activeCategory);
    if (searchQuery) r = r.filter(x => (x.title + x.tags.join(' ')).toLowerCase().includes(searchQuery.toLowerCase()));
    return r;
  }, [reports, activeCategory, searchQuery]);

  const handleGenerate = async () => {
    if (!genForm.title) { Alert.alert('Required', 'Please enter a report title.'); return; }
    setGenerating(true); setGenProgress(0);
    progressAnim.setValue(0);
    Animated.timing(progressAnim, { toValue: 1, duration: 3200, useNativeDriver: false }).start();
    for (let p = 0; p <= 100; p += 10) { await new Promise(r => setTimeout(r, 320)); setGenProgress(p); }
    const newReport = {
      id: `R${Date.now()}`, title: genForm.title, category: genForm.category,
      type: 'custom', period: genForm.period, size: '1.2 MB', format: genForm.format,
      status: 'ready', generated: new Date().toLocaleString(), color: REPORT_CATEGORIES.find(c => c.id === genForm.category)?.color || BRAND.blue,
      icon: 'file-chart', scheduled: false, tags: [genForm.category, 'custom'],
    };
    if (genForm.includeAI) {
      const summary = await askReportsAI(`Summarise the key insights for a ${genForm.period} ${genForm.category} report titled "${genForm.title}".`);
      newReport.aiSummary = summary;
    }
    setReports(prev => [newReport, ...prev]);
    setGenerating(false); setShowGenerate(false);
    setGenForm({ title: '', category: 'finance', format: 'PDF', period: 'Monthly', includeCharts: true, includeAI: true });
    Alert.alert('✅ Report Generated', genForm.includeAI ? `"${newReport.title}" is ready with AI insights.` : `"${newReport.title}" is ready to download.`);
  };

  const handleExport = async (report) => {
    await Share.share({ message: `${report.title}\nFormat: ${report.format}\nGenerated: ${report.generated}\nSize: ${report.size}\nCategory: ${String(report.category ?? '').toUpperCase()}\nTags: ${report.tags.join(', ')}\n\nExported from JobEng ERP · ${new Date().toLocaleString()}`, title: report.title });
  };

  const handleDelete = (report) => {
    Alert.alert('Delete Report', `Remove "${report.title}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => { setReports(prev => prev.filter(r => r.id !== report.id)); setShowDetail(false); } },
    ]);
  };

  const catColor = (id) => REPORT_CATEGORIES.find(c => c.id === id)?.color || BRAND.blue;

  const renderReportCard = (report) => {
    const fmt = FORMAT_INFO[report.format] || FORMAT_INFO.PDF;
    return (
      <TouchableOpacity key={report.id} style={[rs.reportCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line, borderLeftColor: report.color, borderLeftWidth: 4 }]} onPress={() => { setSelected(report); setShowDetail(true); }} activeOpacity={0.75}>
        <View style={[rs.reportIconBox, { backgroundColor: report.color + '18' }]}>
          <MaterialCommunityIcons name={report.icon} size={22} color={report.color} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[rs.reportTitle, { color: BRAND.text }]} numberOfLines={1}>{report.title}</Text>
          <View style={{ flexDirection: 'row', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            <View style={[rs.catChip, { backgroundColor: report.color + '15' }]}><Text style={[rs.catChipText, { color: report.color }]}>{String(report.category ?? '').toUpperCase()}</Text></View>
            <Text style={[rs.reportMeta, { color: BRAND.sub }]}>{report.period}</Text>
            {report.scheduled && <MaterialCommunityIcons name="calendar-clock" size={12} color={BRAND.mint} />}
          </View>
          {report.generated && <Text style={[rs.reportDate, { color: BRAND.sub }]}>{report.generated}</Text>}
        </View>
        <View style={{ alignItems: 'flex-end', gap: 6 }}>
          <View style={[rs.fmtBadge, { backgroundColor: fmt.color + '18' }]}>
            <MaterialCommunityIcons name={fmt.icon} size={11} color={fmt.color} />
            <Text style={[rs.fmtText, { color: fmt.color }]}>{report.format}</Text>
          </View>
          <View style={[rs.statusDot, { backgroundColor: report.status === 'ready' ? BRAND.success : report.status === 'processing' ? BRAND.warning : BRAND.line }]}>
            <Text style={rs.statusText}>{report.status}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderLibrary = () => (
    <View style={{ flex: 1 }}>
      {/* Search + generate */}
      <View style={[rs.toolbar, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
        <View style={[rs.searchBox, { backgroundColor: BRAND.bg, borderColor: BRAND.line }]}>
          <MaterialCommunityIcons name="magnify" size={16} color={BRAND.sub} />
          <TextInput value={searchQuery} onChangeText={setSearchQuery} placeholder="Search reports…" placeholderTextColor={BRAND.sub} style={[rs.searchInput, { color: BRAND.text }]} />
          {searchQuery ? <TouchableOpacity onPress={() => setSearchQuery('')}><MaterialCommunityIcons name="close-circle" size={14} color={BRAND.sub} /></TouchableOpacity> : null}
        </View>
        <TouchableOpacity style={[rs.generateBtn, { backgroundColor: BRAND.blue }]} onPress={() => setShowGenerate(true)}>
          <MaterialCommunityIcons name="plus" size={16} color="#fff" />
          <Text style={rs.generateBtnText}>Generate</Text>
        </TouchableOpacity>
      </View>

      {/* Category chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingLeft: 16, paddingVertical: 10, maxHeight: 50 }}>
        {REPORT_CATEGORIES.map(cat => (
          <TouchableOpacity key={cat.id} style={[rs.catPill, activeCategory === cat.id && { backgroundColor: (cat.color || BRAND.blue), borderColor: (cat.color || BRAND.blue) }, { borderColor: BRAND.line }]} onPress={() => setActiveCategory(cat.id)}>
            <MaterialCommunityIcons name={cat.icon} size={12} color={activeCategory === cat.id ? '#fff' : BRAND.sub} />
            <Text style={[rs.catPillText, { color: activeCategory === cat.id ? '#fff' : BRAND.sub }]}>{cat.label}</Text>
          </TouchableOpacity>
        ))}
        <View style={{ width: 16 }} />
      </ScrollView>

      {/* Stats row */}
      <View style={[rs.statsRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
        {[
          { v: filtered.length, l: 'Reports', c: BRAND.blue },
          { v: filtered.filter(r => r.status === 'ready').length, l: 'Ready', c: BRAND.success },
          { v: filtered.filter(r => r.scheduled).length, l: 'Scheduled', c: BRAND.mint },
          { v: filtered.filter(r => r.status === 'processing').length, l: 'Processing', c: BRAND.warning },
        ].map((s, i) => (
          <React.Fragment key={i}>
            <View style={rs.statItem}><Text style={[rs.statValue, { color: s.c }]}>{s.v}</Text><Text style={[rs.statLabel, { color: BRAND.sub }]}>{s.l}</Text></View>
            {i < 3 && <View style={[rs.statDivider, { backgroundColor: BRAND.line }]} />}
          </React.Fragment>
        ))}
      </View>

      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); setTimeout(() => setRefreshing(false), 800); }} tintColor={BRAND.blue} />} style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
        {filtered.length === 0 ? (
          <View style={rs.empty}><MaterialCommunityIcons name="file-search" size={52} color={BRAND.line} /><Text style={[rs.emptyTitle, { color: BRAND.text }]}>No reports found</Text><Text style={[rs.emptySub, { color: BRAND.sub }]}>Try a different filter or generate a new report</Text></View>
        ) : (
          filtered.map(r => renderReportCard(r))
        )}
      </ScrollView>
    </View>
  );

  const renderScheduled = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 80 }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); setTimeout(() => setRefreshing(false), 800); }} tintColor={BRAND.blue} />} showsVerticalScrollIndicator={false}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text style={[rs.sectionTitle, { color: BRAND.text }]}>Scheduled Reports ({SCHEDULED.length})</Text>
        <TouchableOpacity style={[rs.addScheduleBtn, { backgroundColor: BRAND.blue }]} onPress={() => setShowSchedule(true)}>
          <MaterialCommunityIcons name="plus" size={14} color="#fff" />
          <Text style={rs.addScheduleBtnText}>Add</Text>
        </TouchableOpacity>
      </View>
      {SCHEDULED.map((s, i) => (
        <View key={i} style={[rs.schedCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line, borderLeftColor: s.color, borderLeftWidth: 4 }]}>
          <View style={{ flex: 1 }}>
            <Text style={[rs.schedName, { color: BRAND.text }]}>{s.name}</Text>
            <Text style={[rs.schedModule, { color: BRAND.sub }]}>{s.module} Module</Text>
          </View>
          <View style={{ alignItems: 'flex-end', gap: 4 }}>
            <View style={[rs.freqBadge, { backgroundColor: BRAND.blue + '15' }]}><Text style={[rs.freqText, { color: BRAND.blue }]}>{s.freq}</Text></View>
            <Text style={[rs.nextRun, { color: s.color }]}>Next: {s.next}</Text>
          </View>
          <View style={{ marginLeft: 12 }}>
            <Switch value={true} onValueChange={() => {}} trackColor={{ false: BRAND.line, true: s.color + '60' }} thumbColor={s.color} style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }} />
          </View>
        </View>
      ))}

      {/* Delivery settings */}
      <Text style={[rs.sectionTitle, { color: BRAND.text, marginTop: 10 }]}>Delivery Settings</Text>
      {[
        { label: 'Email to Managers', sub: 'CEO, CFO, COO on report ready', icon: 'email', enabled: true, color: BRAND.blue },
        { label: 'Push Notifications', sub: 'App notification when generated', icon: 'bell', enabled: true, color: BRAND.purple },
        { label: 'Slack Integration', sub: 'Post summary to #reports channel', icon: 'slack', enabled: false, color: '#4A154B' },
        { label: 'Auto-Archive (30 days)', sub: 'Move old reports to archive', icon: 'archive', enabled: true, color: BRAND.warning },
      ].map((d, i) => (
        <View key={i} style={[rs.deliveryRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
          <View style={[rs.deliveryIcon, { backgroundColor: d.color + '18' }]}><MaterialCommunityIcons name={d.icon} size={18} color={d.color} /></View>
          <View style={{ flex: 1 }}>
            <Text style={[rs.deliveryLabel, { color: BRAND.text }]}>{d.label}</Text>
            <Text style={[rs.deliverySub, { color: BRAND.sub }]}>{d.sub}</Text>
          </View>
          <Switch value={d.enabled} onValueChange={() => {}} trackColor={{ false: BRAND.line, true: d.color + '60' }} thumbColor={d.color} style={{ transform: [{ scaleX: 0.85 }, { scaleY: 0.85 }] }} />
        </View>
      ))}
    </ScrollView>
  );

  const renderTemplates = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 80 }} showsVerticalScrollIndicator={false}>
      <Text style={[rs.sectionTitle, { color: BRAND.text, marginBottom: 16 }]}>Report Templates</Text>
      {[
        { name: 'Executive Dashboard', desc: 'CEO-level KPIs across all modules', icon: 'view-dashboard', color: BRAND.blue, uses: 24, tags: ['executive', 'kpis'] },
        { name: 'Board Pack', desc: 'Quarterly board presentation bundle', icon: 'briefcase', color: BRAND.purple, uses: 8, tags: ['board', 'quarterly'] },
        { name: 'Investor Report', desc: 'Revenue, growth, and projections', icon: 'chart-areaspline', color: BRAND.success, uses: 12, tags: ['investors', 'growth'] },
        { name: 'HMRC VAT Return', desc: 'VAT calculation and submission data', icon: 'bank-outline', color: '#1d4ed8', uses: 4, tags: ['HMRC', 'VAT', 'compliance'] },
        { name: 'P&L Summary', desc: 'Monthly profit and loss overview', icon: 'cash-multiple', color: BRAND.success, uses: 18, tags: ['finance', 'monthly'] },
        { name: 'Team Performance', desc: 'All department KPIs and reviews', icon: 'account-group', color: BRAND.mint, uses: 15, tags: ['HR', 'performance'] },
        { name: 'Supply Chain Risk', desc: 'Inventory, suppliers, lead times', icon: 'truck', color: BRAND.warning, uses: 6, tags: ['inventory', 'procurement'] },
        { name: 'AI Business Insights', desc: 'Claude-powered cross-module analysis', icon: 'robot', color: '#6366f1', uses: 31, tags: ['AI', 'insights'] },
      ].map((tmpl, i) => (
        <TouchableOpacity key={i} style={[rs.tmplCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]} onPress={() => { setGenForm(prev => ({ ...prev, title: tmpl.name })); setShowGenerate(true); }}>
          <View style={[rs.tmplIcon, { backgroundColor: tmpl.color + '18' }]}><MaterialCommunityIcons name={tmpl.icon} size={24} color={tmpl.color} /></View>
          <View style={{ flex: 1 }}>
            <Text style={[rs.tmplName, { color: BRAND.text }]}>{tmpl.name}</Text>
            <Text style={[rs.tmplDesc, { color: BRAND.sub }]}>{tmpl.desc}</Text>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {tmpl.tags.map((t, ti) => (
                <View key={ti} style={[rs.tag, { backgroundColor: tmpl.color + '12' }]}><Text style={[rs.tagText, { color: tmpl.color }]}>{t}</Text></View>
              ))}
            </View>
          </View>
          <View style={{ alignItems: 'center', gap: 4 }}>
            <Text style={[rs.uses, { color: BRAND.sub }]}>{tmpl.uses}</Text>
            <Text style={[rs.usesLabel, { color: BRAND.sub }]}>uses</Text>
            <MaterialCommunityIcons name="chevron-right" size={18} color={BRAND.line} />
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  const TABS = [
    { id: 'library', label: 'Library', icon: 'folder-multiple' },
    { id: 'scheduled', label: 'Scheduled', icon: 'calendar-clock' },
    { id: 'templates', label: 'Templates', icon: 'file-document-multiple' },
  ];

  return (
    <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      <LinearGradient colors={['#0e4e96', '#163b6c']} style={rs.header}>
        <View style={rs.headerRow}>
          <TouchableOpacity style={rs.backBtn} onPress={() => navigation?.goBack()}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
          <View style={[rs.headerIcon, { backgroundColor: 'rgba(255,255,255,0.2)' }]}><MaterialCommunityIcons name="chart-box" size={20} color="#fff" /></View>
          <View style={{ flex: 1 }}>
            <Text style={rs.headerTitle}>Reports Centre</Text>
            <Text style={rs.headerSub}>{reports.length} reports · {SCHEDULED.length} scheduled</Text>
          </View>
          <TouchableOpacity style={rs.headerBtn} onPress={() => setShowGenerate(true)}><MaterialCommunityIcons name="plus" size={20} color="#fff" /></TouchableOpacity>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8, marginBottom: -6 }}>
          {TABS.map(tab => (
            <TouchableOpacity key={tab.id} style={[rs.tab, activeTab === tab.id && rs.tabActive]} onPress={() => setActiveTab(tab.id)}>
              <MaterialCommunityIcons name={tab.icon} size={13} color={activeTab === tab.id ? BRAND.blue : 'rgba(255,255,255,0.75)'} />
              <Text style={[rs.tabText, activeTab === tab.id && { color: BRAND.blue }]}>{tab.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </LinearGradient>

      <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
        {activeTab === 'library' && renderLibrary()}
        {activeTab === 'scheduled' && renderScheduled()}
        {activeTab === 'templates' && renderTemplates()}
      </Animated.View>

      {/* REPORT DETAIL MODAL */}
      <Modal visible={showDetail} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDetail(false)}>
        <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
          {selected && (
            <>
              <LinearGradient colors={[selected.color, selected.color + 'aa']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12, paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) + 16 : 16 }}>
                <TouchableOpacity style={rs.modalClose} onPress={() => setShowDetail(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
                <Text style={[rs.modalTitle, { flex: 1, color: '#fff' }]} numberOfLines={1}>{selected.title}</Text>
              </LinearGradient>
              <ScrollView style={{ flex: 1, padding: 16 }}>
                <View style={[rs.detailHeader, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
                  <View style={[rs.detailIconBox, { backgroundColor: selected.color + '18' }]}><MaterialCommunityIcons name={selected.icon} size={32} color={selected.color} /></View>
                  <View style={{ flex: 1 }}>
                    <View style={[rs.catChip, { backgroundColor: selected.color + '18', alignSelf: 'flex-start', marginBottom: 6 }]}><Text style={[rs.catChipText, { color: selected.color }]}>{String(selected.category ?? '').toUpperCase()}</Text></View>
                    <Text style={[rs.detailType, { color: BRAND.sub }]}>{selected.type} · {selected.period}</Text>
                  </View>
                </View>
                <View style={[rs.detailMeta, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
                  {[
                    { l: 'Format', v: selected.format, i: FORMAT_INFO[selected.format]?.icon || 'file' },
                    { l: 'File Size', v: selected.size, i: 'database' },
                    { l: 'Generated', v: selected.generated || 'Not yet run', i: 'clock' },
                    { l: 'Scheduled', v: selected.scheduled ? `Yes — ${selected.schedFreq}` : 'No', i: 'calendar-clock' },
                    { l: 'Status', v: selected.status, i: 'flag' },
                  ].map((f, i) => (
                    <View key={i} style={[rs.metaRow, { borderBottomColor: BRAND.line, borderBottomWidth: i < 4 ? 1 : 0 }]}>
                      <MaterialCommunityIcons name={f.i} size={14} color={BRAND.sub} style={{ marginRight: 8 }} />
                      <Text style={[rs.metaLabel, { color: BRAND.sub }]}>{f.l}</Text>
                      <Text style={[rs.metaValue, { color: BRAND.text }]}>{f.v}</Text>
                    </View>
                  ))}
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                  {(selected.tags || []).map((t, ti) => <View key={ti} style={[rs.tag, { backgroundColor: selected.color + '12' }]}><Text style={[rs.tagText, { color: selected.color }]}>{t}</Text></View>)}
                </View>
                <View style={rs.detailActions}>
                  {[
                    { l: 'Download', i: 'download', c: selected.color, onPress: () => handleExport(selected) },
                    { l: 'Share', i: 'share', c: BRAND.mint, onPress: () => handleExport(selected) },
                    { l: 'Schedule', i: 'calendar-clock', c: BRAND.blue, onPress: () => setShowSchedule(true) },
                    { l: 'Delete', i: 'delete', c: BRAND.danger, onPress: () => handleDelete(selected) },
                  ].map((a, i) => (
                    <TouchableOpacity key={i} style={[rs.actionBtn, { borderColor: a.c + '40', backgroundColor: a.c + '10', flex: 1, minWidth: (W - 64) / 2 }]} onPress={a.onPress}>
                      <MaterialCommunityIcons name={a.i} size={16} color={a.c} />
                      <Text style={[rs.actionBtnText, { color: a.c }]}>{a.l}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </>
          )}
        </View>
      </Modal>

      {/* GENERATE MODAL */}
      <Modal visible={showGenerate} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => { if (!generating) setShowGenerate(false); }}>
        <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
          <LinearGradient colors={[BRAND.blue, '#163b6c']} style={{ flexDirection: 'row', alignItems: 'center', padding: 16, gap: 12, paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) + 16 : 16 }}>
            <TouchableOpacity style={rs.modalClose} onPress={() => { if (!generating) setShowGenerate(false); }}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
            <Text style={[rs.modalTitle, { flex: 1, color: '#fff' }]}>Generate Report</Text>
          </LinearGradient>
          <ScrollView style={{ flex: 1, padding: 16 }}>
            {generating ? (
              <View style={{ alignItems: 'center', paddingVertical: 48 }}>
                <MaterialCommunityIcons name="file-chart" size={64} color={BRAND.blue} />
                <Text style={[rs.sectionTitle, { color: BRAND.text, marginTop: 20, marginBottom: 8 }]}>Generating Report…</Text>
                <Text style={{ color: BRAND.sub, fontSize: 13, marginBottom: 24 }}>{genForm.title}</Text>
                <View style={{ width: W - 64, height: 10, backgroundColor: BRAND.line, borderRadius: 5, overflow: 'hidden', marginBottom: 12 }}>
                  <Animated.View style={{ height: '100%', borderRadius: 5, backgroundColor: BRAND.blue, width: progressAnim.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }) }} />
                </View>
                <Text style={{ color: BRAND.blue, fontWeight: '700', fontSize: 14 }}>{genProgress}%</Text>
                {genProgress > 30 && <Text style={{ color: BRAND.sub, fontSize: 11, marginTop: 8 }}>Collecting data from modules…</Text>}
                {genProgress > 60 && <Text style={{ color: BRAND.sub, fontSize: 11, marginTop: 4 }}>Applying AI analysis…</Text>}
                {genProgress > 85 && <Text style={{ color: BRAND.sub, fontSize: 11, marginTop: 4 }}>Rendering charts…</Text>}
              </View>
            ) : (
              <>
                {[
                  { k: 'title', l: 'Report Title *', i: 'file-chart', ph: 'e.g. Q1 2025 Financial Review' },
                ].map(f => (
                  <View key={f.k} style={{ marginBottom: 14 }}>
                    <Text style={[rs.fieldLabel, { color: BRAND.text }]}>{f.l}</Text>
                    <View style={[rs.fieldBox, { backgroundColor: BRAND.surface, borderColor: genForm[f.k] ? BRAND.blue : BRAND.line }]}>
                      <MaterialCommunityIcons name={f.i} size={16} color={BRAND.sub} />
                      <TextInput value={genForm[f.k]} onChangeText={v => setGenForm(p => ({ ...p, [f.k]: v }))} placeholder={f.ph} placeholderTextColor={BRAND.sub} style={[rs.fieldInput, { color: BRAND.text }]} />
                    </View>
                  </View>
                ))}
                <Text style={[rs.fieldLabel, { color: BRAND.text }]}>Module</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
                  {REPORT_CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                    <TouchableOpacity key={cat.id} style={[rs.catPill, genForm.category === cat.id && { backgroundColor: cat.color, borderColor: cat.color }, { borderColor: BRAND.line, marginRight: 8 }]} onPress={() => setGenForm(p => ({ ...p, category: cat.id }))}>
                      <MaterialCommunityIcons name={cat.icon} size={12} color={genForm.category === cat.id ? '#fff' : BRAND.sub} />
                      <Text style={[rs.catPillText, { color: genForm.category === cat.id ? '#fff' : BRAND.sub }]}>{cat.label}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
                <Text style={[rs.fieldLabel, { color: BRAND.text }]}>Format</Text>
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                  {['PDF', 'Excel', 'CSV', 'JSON'].map(fmt => {
                    const f = FORMAT_INFO[fmt];
                    return (
                      <TouchableOpacity key={fmt} style={[rs.fmtOption, genForm.format === fmt && { backgroundColor: f.color, borderColor: f.color }, { borderColor: BRAND.line }]} onPress={() => setGenForm(p => ({ ...p, format: fmt }))}>
                        <MaterialCommunityIcons name={f.icon} size={16} color={genForm.format === fmt ? '#fff' : f.color} />
                        <Text style={[rs.fmtOptionText, { color: genForm.format === fmt ? '#fff' : BRAND.sub }]}>{fmt}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                <Text style={[rs.fieldLabel, { color: BRAND.text }]}>Period</Text>
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                  {['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Annually', 'Custom'].map(p => (
                    <TouchableOpacity key={p} style={[rs.periodPill, genForm.period === p && { backgroundColor: BRAND.blue, borderColor: BRAND.blue }, { borderColor: BRAND.line }]} onPress={() => setGenForm(prev => ({ ...prev, period: p }))}>
                      <Text style={[rs.periodPillText, { color: genForm.period === p ? '#fff' : BRAND.sub }]}>{p}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                <View style={[rs.toggleRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
                  <View style={{ flex: 1 }}>
                    <Text style={[rs.toggleLabel, { color: BRAND.text }]}>Include Charts & Graphs</Text>
                    <Text style={[rs.toggleSub, { color: BRAND.sub }]}>Visual data representations</Text>
                  </View>
                  <Switch value={genForm.includeCharts} onValueChange={v => setGenForm(p => ({ ...p, includeCharts: v }))} trackColor={{ false: BRAND.line, true: BRAND.blue + '60' }} thumbColor={BRAND.blue} />
                </View>
                <View style={[rs.toggleRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line, marginBottom: 24 }]}>
                  <View style={{ flex: 1 }}>
                    <Text style={[rs.toggleLabel, { color: BRAND.text }]}>AI Analysis & Insights</Text>
                    <Text style={[rs.toggleSub, { color: BRAND.sub }]}>Claude-powered commentary</Text>
                  </View>
                  <Switch value={genForm.includeAI} onValueChange={v => setGenForm(p => ({ ...p, includeAI: v }))} trackColor={{ false: BRAND.line, true: '#6366f1' + '60' }} thumbColor={'#6366f1'} />
                </View>
                <TouchableOpacity style={[rs.genBtn, { backgroundColor: BRAND.blue }]} onPress={handleGenerate}>
                  <MaterialCommunityIcons name="file-chart" size={18} color="#fff" />
                  <Text style={rs.genBtnText}>Generate Report</Text>
                </TouchableOpacity>
              </>
            )}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const rs = StyleSheet.create({
  header: { paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) : 0, paddingHorizontal: 16, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  headerIcon: { width: 36, height: 36, borderRadius: 11, justifyContent: 'center', alignItems: 'center' },
  headerTitle: { color: '#fff', fontSize: 16, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 1 },
  headerBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 18, marginRight: 6, marginBottom: 8 },
  tabActive: { backgroundColor: '#fff' },
  tabText: { color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: '600' },
  toolbar: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderBottomWidth: 1 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 10, borderWidth: 1 },
  searchInput: { flex: 1, fontSize: 13 },
  generateBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 10 },
  generateBtnText: { color: '#fff', fontSize: 13, fontWeight: '700' },
  catPill: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1, marginRight: 8 },
  catPillText: { fontSize: 11, fontWeight: '600' },
  statsRow: { flexDirection: 'row', borderBottomWidth: 1, borderTopWidth: 1, paddingVertical: 10 },
  statItem: { flex: 1, alignItems: 'center' },
  statValue: { fontSize: 17, fontWeight: '900' },
  statLabel: { fontSize: 10, marginTop: 1 },
  statDivider: { width: 1, marginVertical: 4 },
  reportCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 13, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 5 },
  reportIconBox: { width: 46, height: 46, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  reportTitle: { fontSize: 13, fontWeight: '700', marginBottom: 2 },
  reportMeta: { fontSize: 10 },
  reportDate: { fontSize: 10, marginTop: 4 },
  catChip: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 6 },
  catChipText: { fontSize: 9, fontWeight: '800', letterSpacing: 0.5 },
  fmtBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, paddingHorizontal: 7, paddingVertical: 3, borderRadius: 7 },
  fmtText: { fontSize: 10, fontWeight: '700' },
  statusDot: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 8 },
  statusText: { color: '#fff', fontSize: 9, fontWeight: '700' },
  empty: { alignItems: 'center', paddingVertical: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '700', marginTop: 14, marginBottom: 6 },
  emptySub: { fontSize: 13, textAlign: 'center' },
  sectionTitle: { fontSize: 15, fontWeight: '700' },
  schedCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  schedName: { fontSize: 13, fontWeight: '700', marginBottom: 3 },
  schedModule: { fontSize: 11 },
  freqBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  freqText: { fontSize: 10, fontWeight: '700' },
  nextRun: { fontSize: 11, fontWeight: '600' },
  addScheduleBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 10 },
  addScheduleBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },
  deliveryRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  deliveryIcon: { width: 38, height: 38, borderRadius: 11, justifyContent: 'center', alignItems: 'center' },
  deliveryLabel: { fontSize: 13, fontWeight: '700', marginBottom: 2 },
  deliverySub: { fontSize: 11 },
  tmplCard: { flexDirection: 'row', alignItems: 'flex-start', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  tmplIcon: { width: 48, height: 48, borderRadius: 14, justifyContent: 'center', alignItems: 'center' },
  tmplName: { fontSize: 14, fontWeight: '700', marginBottom: 3 },
  tmplDesc: { fontSize: 11 },
  tag: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 7 },
  tagText: { fontSize: 10, fontWeight: '600' },
  uses: { fontSize: 16, fontWeight: '800' },
  usesLabel: { fontSize: 9 },
  modalClose: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  modalTitle: { fontSize: 16, fontWeight: '700' },
  detailHeader: { flexDirection: 'row', alignItems: 'center', gap: 14, padding: 16, borderRadius: 16, borderWidth: 1, marginBottom: 14, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 5 },
  detailIconBox: { width: 56, height: 56, borderRadius: 16, justifyContent: 'center', alignItems: 'center' },
  detailType: { fontSize: 12, textTransform: 'capitalize' },
  detailMeta: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', marginBottom: 14, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  metaRow: { flexDirection: 'row', alignItems: 'center', padding: 13 },
  metaLabel: { flex: 1, fontSize: 12 },
  metaValue: { fontSize: 13, fontWeight: '600' },
  detailActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 30 },
  actionBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12, borderRadius: 12, borderWidth: 1 },
  actionBtnText: { fontSize: 12, fontWeight: '700' },
  fieldLabel: { fontSize: 13, fontWeight: '600', marginBottom: 6 },
  fieldBox: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, borderWidth: 1 },
  fieldInput: { flex: 1, fontSize: 14 },
  fmtOption: { flex: 1, alignItems: 'center', gap: 4, paddingVertical: 10, borderRadius: 10, borderWidth: 1 },
  fmtOptionText: { fontSize: 11, fontWeight: '600' },
  periodPill: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, borderWidth: 1 },
  periodPillText: { fontSize: 12, fontWeight: '600' },
  toggleRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 14, borderWidth: 1, marginBottom: 10 },
  toggleLabel: { fontSize: 13, fontWeight: '600', marginBottom: 2 },
  toggleSub: { fontSize: 11 },
  genBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16, borderRadius: 16 },
  genBtnText: { color: '#fff', fontSize: 16, fontWeight: '700' },
});

export default ERPReportsScreen;